// src/components/payments/PayPalSubscriptionService.jsx
import {
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { EntitlementCalculator } from "./RequireEntitlement";

const toTs = (iso) => (iso ? Timestamp.fromDate(new Date(iso)) : null);
const safeNumber = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};
const normalizeNull = (value) => (value === undefined ? null : value);

const isActiveStatus = (status) =>
  ["ACTIVE", "TRIALING", "APPROVAL_PENDING"].includes(
    String(status || "").toUpperCase()
  );

function sanitizeSubPayload(rawPayload = {}) {
  const flattenObject = (object = {}) =>
    Object.fromEntries(
      Object.entries(object).map(([key, value]) => [
        key,
        value === undefined ? null : value,
      ])
    );
  return {
    ...flattenObject({
      provider: rawPayload.provider,
      subscriptionId: rawPayload.subscriptionId,
      planId: rawPayload.planId,
      planName: rawPayload.planName,
      status: rawPayload.status,
      statusUpdatedAt: rawPayload.statusUpdatedAt,
      startTime: rawPayload.startTime,
      nextBillingTime: rawPayload.nextBillingTime,
      nextBillingDateAppears: rawPayload.nextBillingDateAppears,
      email: rawPayload.email,
      payerId: rawPayload.payerId,
      country: rawPayload.country,
    }),
    lastPayment: flattenObject(rawPayload.lastPayment),
  };
}

export async function loadRuntimePaymentsConfig(db, userId) {
  if (userId) {
    try {
      const userSnapshot = await getDoc(doc(db, "users", userId));
      if (userSnapshot.exists()) {
        const userData = userSnapshot.data() || {};
        const userPaymentsConfig =
          userData.paymentsConfig || userData.settings?.payments || {};
        const userPaypalConfig = userPaymentsConfig.paypal || {};
        const apiBase = String(userPaymentsConfig.apiBase || "").replace(
          /\/+$/,
          ""
        );
        const paypalClientId =
          userPaypalConfig.clientId || userPaymentsConfig.paypalClientId || "";
        if (apiBase || paypalClientId) {
          return { apiBase, paypalClientId };
        }
      }
    } catch {
      // ignored, fallback below
    }
  }

  try {
    const configSnapshot = await getDoc(doc(db, "config", "public"));
    if (configSnapshot.exists()) {
      const paymentsConfig = configSnapshot.data()?.payments || {};
      const paypalConfig = paymentsConfig?.paypal || {};
      return {
        apiBase: String(paymentsConfig.apiBase || "").replace(/\/+$/, ""),
        paypalClientId: paypalConfig.clientId || "",
      };
    }
  } catch {
    // ignored, fallback below
  }

  return {
    apiBase: String(process.env.REACT_APP_API_BASE || "").replace(/\/+$/, ""),
    paypalClientId: process.env.REACT_APP_PAYPAL_CLIENT_ID || "",
  };
}

let paypalSdkPromise = null;
export function loadPayPalSDKDynamic(clientId) {
  if (paypalSdkPromise) return paypalSdkPromise;
  paypalSdkPromise = new Promise((resolve, reject) => {
    if (window.paypal?.Buttons) return resolve(window.paypal);
    if (!clientId) return reject(new Error("Missing PayPal clientId"));
    const url = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(
      clientId
    )}&components=buttons&intent=subscription&vault=true`;
    const scriptElement = document.createElement("script");
    scriptElement.src = url;
    scriptElement.async = true;
    scriptElement.onload = () =>
      window.paypal?.Buttons
        ? resolve(window.paypal)
        : reject(new Error("PayPal Buttons missing"));
    scriptElement.onerror = () => {
      paypalSdkPromise = null;
      reject(new Error("Failed to load PayPal SDK."));
    };
    document.head.appendChild(scriptElement);
  });
  return paypalSdkPromise;
}

export async function fetchPayPalSubscription(apiBase, subscriptionId) {
  const base = apiBase || "";
  const endpoint = `${base}/api/paypal/subscriptions/${encodeURIComponent(
    subscriptionId
  )}`;
  const response = await fetch(endpoint, {
    headers: { Accept: "application/json" },
  });
  const text = await response.text();
  let json;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error(`Non-JSON backend response (status ${response.status})`);
  }
  if (!response.ok) throw new Error(json?.message || `Backend ${response.status}`);
  return json;
}

export function mapPayPalToSubPayload(paypalSubscription = {}, planMeta = {}) {
  const billingInfo =
    paypalSubscription?.billing_info || paypalSubscription?.billingInfo || {};
  const lastPaymentRaw =
    billingInfo?.last_payment || paypalSubscription?.last_payment || {};
  const amountObject =
    lastPaymentRaw?.amount ||
    paypalSubscription?.last_payment_amount ||
    paypalSubscription?.amount ||
    {};

  const nextBillingIso =
    billingInfo?.next_billing_time ||
    billingInfo?.nextBillingTime ||
    paypalSubscription?.next_billing_time ||
    paypalSubscription?.nextBillingTime ||
    null;

  const lastPaymentTimeIso =
    lastPaymentRaw?.time ||
    paypalSubscription?.last_payment_time ||
    paypalSubscription?.lastPaymentTime ||
    null;

  const statusUpdateIso =
    paypalSubscription?.status_update_time ||
    paypalSubscription?.update_time ||
    paypalSubscription?.statusUpdateTime ||
    null;

  const startIso =
    paypalSubscription?.start_time ||
    paypalSubscription?.create_time ||
    paypalSubscription?.startTime ||
    paypalSubscription?.createTime ||
    null;

  const subscriber = paypalSubscription?.subscriber || {};
  const shippingAddress =
    subscriber?.shipping_address?.address ||
    subscriber?.shippingAddress?.address ||
    {};

  return {
    provider: "paypal",
    subscriptionId: normalizeNull(paypalSubscription?.id),
    planId:
      normalizeNull(paypalSubscription?.plan_id) ||
      normalizeNull(planMeta.planId),
    planName:
      normalizeNull(paypalSubscription?.plan_name) ||
      normalizeNull(planMeta.displayName) ||
      normalizeNull(planMeta.name),

    status: String(paypalSubscription?.status || "UNKNOWN").toUpperCase(),
    statusUpdatedAt: normalizeNull(toTs(statusUpdateIso)),
    startTime: normalizeNull(toTs(startIso)),
    nextBillingTime: normalizeNull(toTs(nextBillingIso)),
    nextBillingDateAppears: Boolean(nextBillingIso),

    lastPayment: {
      amount: normalizeNull(
        safeNumber(amountObject?.value ?? planMeta.price)
      ),
      currency: normalizeNull(
        amountObject?.currency_code ||
          planMeta.currency ||
          paypalSubscription?.currency_code ||
          paypalSubscription?.currency
      ),
      time: normalizeNull(toTs(lastPaymentTimeIso)),
    },

    email: normalizeNull(
      subscriber?.email_address || subscriber?.emailAddress
    ),
    payerId: normalizeNull(subscriber?.payer_id || subscriber?.payerId),
    country: normalizeNull(
      shippingAddress?.country_code || shippingAddress?.countryCode
    ),
  };
}

export function needsRepoll(payload) {
  const missingNextBill = !payload?.nextBillingTime;
  const missingPayTime = !payload?.lastPayment?.time;
  const missingCurrency = !payload?.lastPayment?.currency;
  const missingSubEmail = !payload?.email;
  const missingStatusTimestamp = !payload?.statusUpdatedAt;

  return Boolean(
    missingNextBill ||
      missingPayTime ||
      missingCurrency ||
      missingSubEmail ||
      missingStatusTimestamp
  );
}

export function scheduleNextBillingBackfill() {}

// Insert/update full subscription payload + compute stacking extension
export async function writeUserSubscription(db, userId, subPayload) {
  const userReference = doc(db, "users", userId);

  const cleanedPayload = sanitizeSubPayload(subPayload);

  return runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(userReference);
    const currentUserData = snapshot.exists() ? snapshot.data() || {} : {};

    const existingSubscriptions = Array.isArray(currentUserData.subscriptions)
      ? currentUserData.subscriptions.slice()
      : [];

    const currentDate = new Date();
    const currentTimestamp = Timestamp.now();

    // Calculate "paid coverage until" BEFORE adding/updating this subscription.
    const entitlementCalculator = new EntitlementCalculator(currentDate);
    const subscriptionsBeforePart =
      entitlementCalculator.evaluateSubscriptions(existingSubscriptions);

    const existingPaidSubscriptionsUntil =
      subscriptionsBeforePart.paidEntitlementEndDateLatest instanceof Date
        ? subscriptionsBeforePart.paidEntitlementEndDateLatest
        : null;

    const existingExtensionDate = EntitlementCalculator.asDateOrNull(
      currentUserData.entitlementExtensionUntil
    );

    let previousPaidCoverageUntil = null;
    if (
      existingPaidSubscriptionsUntil &&
      existingPaidSubscriptionsUntil > currentDate
    ) {
      previousPaidCoverageUntil = existingPaidSubscriptionsUntil;
    }
    if (existingExtensionDate && existingExtensionDate > currentDate) {
      if (
        !previousPaidCoverageUntil ||
        existingExtensionDate > previousPaidCoverageUntil
      ) {
        previousPaidCoverageUntil = existingExtensionDate;
      }
    }

    const subscriptions = existingSubscriptions;
    const existingIndex = subscriptions.findIndex(
      (subscription) =>
        subscription?.subscriptionId === cleanedPayload.subscriptionId
    );
    const previousVersion =
      existingIndex >= 0 ? subscriptions[existingIndex] : null;

    const stickyNextBillingFlag =
      Boolean(cleanedPayload.nextBillingTime) ||
      Boolean(previousVersion?.nextBillingDateAppears);

    const newEntry = {
      ...cleanedPayload,
      nextBillingDateAppears: stickyNextBillingFlag,
      updatedAt: currentTimestamp,
    };

    if (existingIndex >= 0) {
      subscriptions[existingIndex] = {
        ...subscriptions[existingIndex],
        ...newEntry,
      };
    } else {
      subscriptions.unshift(newEntry);
    }

    const justWritten =
      existingIndex >= 0 ? subscriptions[existingIndex] : subscriptions[0];
    const justWrittenIsActive = isActiveStatus(justWritten?.status);

    let activeSubscriptionId = null;
    if (justWrittenIsActive) {
      activeSubscriptionId = justWritten.subscriptionId || null;
    } else {
      const otherActiveSubscription = subscriptions.find((subscription) =>
        isActiveStatus(subscription?.status)
      );
      activeSubscriptionId = otherActiveSubscription?.subscriptionId || null;
    }

    const normalizedSubscriptions = subscriptions.map((subscription) => {
      const isActive =
        subscription.subscriptionId === activeSubscriptionId &&
        isActiveStatus(subscription.status);
      return isActive
        ? {
            ...subscription,
            isActive: true,
            active: true,
            activeSubId: activeSubscriptionId,
            gracePeriod: false,
            payingUserAi: true,
          }
        : { ...subscription, isActive: false };
    });

    // True stacking: extend entitlementExtensionUntil when a subscription
    // first becomes entitled while there is already paid coverage.
    let nextExtensionDate = existingExtensionDate || null;

    try {
      const previousSingleEvaluation = previousVersion
        ? entitlementCalculator.evaluateSingleSubscription(previousVersion)
        : { isEntitled: false, entitlementEndDate: null };

      const newSingleEvaluation =
        entitlementCalculator.evaluateSingleSubscription(newEntry);

      const isNewlyEntitled =
        newSingleEvaluation.isEntitled &&
        !previousSingleEvaluation.isEntitled;

      const hasFiniteEntitlementEnd =
        newSingleEvaluation.entitlementEndDate instanceof Date &&
        newSingleEvaluation.entitlementEndDate > currentDate;

      const hasExistingPaidCoverage =
        previousPaidCoverageUntil && previousPaidCoverageUntil > currentDate;

      if (
        isNewlyEntitled &&
        hasFiniteEntitlementEnd &&
        hasExistingPaidCoverage
      ) {
        const newDurationMilliseconds =
          newSingleEvaluation.entitlementEndDate.getTime() -
          currentDate.getTime();

        if (newDurationMilliseconds > 0) {
          const stackedUntil = new Date(
            previousPaidCoverageUntil.getTime() + newDurationMilliseconds
          );

          if (
            !nextExtensionDate ||
            stackedUntil.getTime() > nextExtensionDate.getTime()
          ) {
            nextExtensionDate = stackedUntil;
          }
        }
      }
    } catch {
      // If stacking calculation fails for any reason, we simply skip it;
      // entitlement will still be correct for non-stacked cases.
    }

    const basePayload = {
      subscriptions: normalizedSubscriptions,
      updatedAt: serverTimestamp(),
    };

    if (
      Object.prototype.hasOwnProperty.call(
        currentUserData,
        "isTrialPeriodActive"
      )
    ) {
      basePayload.isTrialPeriodActive = false;
    }

    if (
      nextExtensionDate &&
      (!existingExtensionDate ||
        nextExtensionDate.getTime() !== existingExtensionDate.getTime())
    ) {
      basePayload.entitlementExtensionUntil =
        Timestamp.fromDate(nextExtensionDate);
    }

    transaction.set(userReference, basePayload, { merge: true });

    return { anyActive: Boolean(activeSubscriptionId), activeSubId: activeSubscriptionId };
  });
}

/**
 * WRITE-ONLY helper for actions:
 * - Writes status + activeSubscriptionStatus + timestamps to subscriptions[0]
 * - Does NOT delete or strip any existing fields.
 */
export async function updateUserSubscriptionStatusIndex0(
  db,
  userId,
  newStatus
) {
  const userReference = doc(db, "users", userId);
  const statusUpper = String(newStatus || "").toUpperCase();
  const currentTimestamp = Timestamp.now();

  return runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(userReference);
    if (!snapshot.exists()) return;

    const currentUserData = snapshot.data() || {};
    const subscriptions = Array.isArray(currentUserData.subscriptions)
      ? currentUserData.subscriptions.slice()
      : [];
    if (!subscriptions.length) return;

    const index = 0; // most recent subscription at index 0
    const previous = subscriptions[index] || {};

    const merged = {
      ...previous,
      status: statusUpper,
      activeSubscriptionStatus: statusUpper,
      statusUpdatedAt: currentTimestamp,
      updatedAt: currentTimestamp,
    };

    subscriptions[index] = merged;

    transaction.set(
      userReference,
      {
        subscriptions,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  });
}

export async function clearNextBillingFieldsIfPastDueIndex0(db, userId) {
  const userReference = doc(db, "users", userId);
  const nowDate = new Date();
  const currentTimestamp = Timestamp.now();

  return runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(userReference);
    if (!snapshot.exists()) return;

    const currentUserData = snapshot.data() || {};
    const subscriptions = Array.isArray(currentUserData.subscriptions)
      ? currentUserData.subscriptions.slice()
      : [];
    if (!subscriptions.length) return;

    const index = 0;
    const firstSubscription = subscriptions[index] || {};
    const statusUpper = String(
      firstSubscription.status || firstSubscription.activeSubscriptionStatus || ""
    ).toUpperCase();

    const isInactive =
      statusUpper === "SUSPENDED" || statusUpper === "CANCELLED";
    const rawNextBilling = firstSubscription?.nextBillingTime;
    const nextBillingDate = rawNextBilling?.toDate?.()
      ? rawNextBilling.toDate()
      : rawNextBilling instanceof Date
      ? rawNextBilling
      : null;

    if (!isInactive || !nextBillingDate) return;
    if (nextBillingDate.getTime() > nowDate.getTime()) return;

    subscriptions[index] = {
      ...firstSubscription,
      nextBillingTime: null,
      nextBillingDateAppears: null,
      active: null,
      updatedAt: currentTimestamp,
    };

    transaction.set(
      userReference,
      {
        subscriptions,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  });
}

/**
 * Update status for a specific subscriptionId (not just index 0).
 * This is used when the user cancels a non-primary plan.
 */
export async function updateUserSubscriptionStatusById(
  db,
  userId,
  subscriptionId,
  newStatus
) {
  const userReference = doc(db, "users", userId);
  const statusUpper = String(newStatus || "").toUpperCase();
  const currentTimestamp = Timestamp.now();

  return runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(userReference);
    if (!snapshot.exists()) return;

    const currentUserData = snapshot.data() || {};
    const subscriptions = Array.isArray(currentUserData.subscriptions)
      ? currentUserData.subscriptions.slice()
      : [];
    if (!subscriptions.length) return;

    const index = subscriptions.findIndex(
      (subscription) => subscription?.subscriptionId === subscriptionId
    );
    if (index < 0) return;

    const previous = subscriptions[index] || {};

    const merged = {
      ...previous,
      status: statusUpper,
      activeSubscriptionStatus: statusUpper,
      statusUpdatedAt: currentTimestamp,
      updatedAt: currentTimestamp,
    };

    subscriptions[index] = merged;

    transaction.set(
      userReference,
      {
        subscriptions,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  });
}

/**
 * Clear nextBillingTime / nextBillingDateAppears for a specific subscriptionId
 * once it is cancelled or suspended and past due.
 */
export async function clearNextBillingFieldsIfPastDueById(
  db,
  userId,
  subscriptionId
) {
  const userReference = doc(db, "users", userId);
  const nowDate = new Date();
  const currentTimestamp = Timestamp.now();

  return runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(userReference);
    if (!snapshot.exists()) return;

    const currentUserData = snapshot.data() || {};
    const subscriptions = Array.isArray(currentUserData.subscriptions)
      ? currentUserData.subscriptions.slice()
      : [];
    if (!subscriptions.length) return;

    const index = subscriptions.findIndex(
      (subscription) => subscription?.subscriptionId === subscriptionId
    );
    if (index < 0) return;

    const subscriptionAtIndex = subscriptions[index] || {};
    const statusUpper = String(
      subscriptionAtIndex.status ||
        subscriptionAtIndex.activeSubscriptionStatus ||
        ""
    ).toUpperCase();

    const isInactive =
      statusUpper === "SUSPENDED" || statusUpper === "CANCELLED";
    const rawNextBilling = subscriptionAtIndex?.nextBillingTime;
    const nextBillingDate = rawNextBilling?.toDate?.()
      ? rawNextBilling.toDate()
      : rawNextBilling instanceof Date
      ? rawNextBilling
      : null;

    if (!isInactive || !nextBillingDate) return;
    if (nextBillingDate.getTime() > nowDate.getTime()) return;

    subscriptions[index] = {
      ...subscriptionAtIndex,
      nextBillingTime: null,
      nextBillingDateAppears: null,
      active: null,
      updatedAt: currentTimestamp,
    };

    transaction.set(
      userReference,
      {
        subscriptions,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  });
}

export class NextBillingVerifier {
  constructor({ iso, fetchFreshJson, persistFresh, maxTimeoutMs = 214783647 }) {
    this.iso = iso;
    this.fetchFreshJson = fetchFreshJson;
    this.persistFresh = persistFresh;
    this.maxTimeoutMs = maxTimeoutMs;
    this.chunkTimerId = 0;
    this.finalTimerId = 0;
  }
  start() {
    this.stop();
    if (!this.iso) return;
    const targetTime = new Date(this.iso).getTime();
    const tick = () => {
      const remaining = targetTime - Date.now();
      if (remaining <= 0) return this.fire();
      if (remaining <= this.maxTimeoutMs) {
        this.finalTimerId = setTimeout(
          () => this.fire(),
          Math.max(0, remaining + 250)
        );
      } else {
        this.chunkTimerId = setTimeout(tick, this.maxTimeoutMs);
      }
    };
    tick();
  }
  stop() {
    if (this.chunkTimerId) clearTimeout(this.chunkTimerId);
    if (this.finalTimerId) clearTimeout(this.finalTimerId);
    this.chunkTimerId = this.finalTimerId = 0;
  }
  async fire() {
    try {
      const json = await this.fetchFreshJson();
      await this.persistFresh(json);
    } catch {
      // swallow
    }
  }
}
